import { useEffect, useMemo, useState } from 'react';
import { getCurrentCustomerProfile } from 'services/customerProfileService';

const getAuthToken = ({ token, readCookie }) => (
    token || (typeof readCookie === 'function' ? readCookie() : null) || ''
);

const emptyProfileState = {
    status: 'idle',
    profile: null,
    error: '',
    authToken: '',
};

export default function useCustomerProfile({ token, readCookie }) {
    const resolvedAuthToken = useMemo(() => getAuthToken({ token, readCookie }), [readCookie, token]);
    const [profileState, setProfileState] = useState(emptyProfileState);

    useEffect(() => {
        let isMounted = true;

        if (!resolvedAuthToken) {
            setProfileState({
                status: 'error',
                profile: null,
                error: 'missing_auth',
                authToken: '',
            });
            return () => {
                isMounted = false;
            };
        }

        setProfileState({
            status: 'loading',
            profile: null,
            error: '',
            authToken: resolvedAuthToken,
        });

        getCurrentCustomerProfile({ token: resolvedAuthToken })
            .then((profile) => {
                if (isMounted) {
                    setProfileState({
                        status: 'ready',
                        profile,
                        error: '',
                        authToken: resolvedAuthToken,
                    });
                }
            })
            .catch((error) => {
                if (isMounted) {
                    setProfileState({
                        status: 'error',
                        profile: null,
                        error: error?.message || 'profile_unavailable',
                        authToken: resolvedAuthToken,
                    });
                }
            });

        return () => {
            isMounted = false;
        };
    }, [resolvedAuthToken]);

    const visibleProfileState = useMemo(() => {
        if (!resolvedAuthToken) {
            return {
                status: 'error',
                profile: null,
                error: 'missing_auth',
            };
        }

        if (profileState.authToken !== resolvedAuthToken) {
            return {
                status: 'loading',
                profile: null,
                error: '',
            };
        }

        return {
            status: profileState.status,
            profile: profileState.profile,
            error: profileState.error,
        };
    }, [profileState, resolvedAuthToken]);

    const analysisClient = visibleProfileState.status === 'ready'
        ? String(visibleProfileState.profile?.analysisClient || '').trim()
        : '';
    const isProfilePending = visibleProfileState.status === 'idle' || visibleProfileState.status === 'loading';
    const isAnalysisScopeReady = visibleProfileState.status === 'ready' && Boolean(analysisClient);

    return {
        profileState: visibleProfileState,
        analysisClient,
        isProfilePending,
        isAnalysisScopeReady,
    };
}
